import {
  useCompanionTooltipsDisabled,
  useSetCompanionTooltipsDisabled,
} from "@hooks/use-companion-tooltips-disabled";
import { useLocalStorage } from "@scribenote/utils";
import mixpanel from "mixpanel-browser";

import { AccountItemLine } from "./AccountItem";
import DarkModeToggle from "./DarkModeToggle";
import FeatureToggle from "./FeatureToggle";
import { alert } from "../common/Alert";
import { DSToggle } from "../design-system/molecules";

export default function PreferencesSettings() {
  return (
    <div id="preferences">
      <div className="flex flex-col flex-1 w-full space-y-4 divide-y dark:divide-gray-700">
        <AccountItemLine>
          <div className="dark:text-gray-300 text-xs md:text-sm lg:w-96 mr-4">
            <strong>Appearance:</strong> Choose Light, Dark, or follow
            your system setting.
          </div>
          <DarkModeToggle />
        </AccountItemLine>
        <AccountItemLine>
          <div className="dark:text-gray-300 text-xs md:text-sm lg:w-96 mr-4">
            <strong>Capitalize SOAP Titles:</strong> When you copy an
            entire SOAP, this setting will determine if the S, O, A,
            and P titles are capitalized for readability.
          </div>
          <CapitalFormatToggle />
        </AccountItemLine>

        <AccountItemLine dataTestId="preferences-copy-submit-line">
          <div className="text-secondary text-xs md:text-sm lg:w-96 mr-md">
            <strong className="text-primary">
              Copy Note when marking as Done:
            </strong>{" "}
            Turning this setting on will automatically copy the note
            to your clipboard when you click "Mark as Done".
          </div>
          <CopySubmitToggle />
        </AccountItemLine>

        <FeatureToggle
          title="Use Plain Text Editor"
          description="Switch back to the classic plain text note inputs instead of the rich text editor."
          flagKey="configureWebRichTextEditorOptOut"
          segmentKey="configure-web-rich-text-editor-opt-out"
          dataTestId="preferences-plain-editor-line"
        />
        <AccountItemLine dataTestId="preferences-companion-tooltips-line">
          <div className="text-secondary text-xs md:text-sm lg:w-96 mr-md">
            <strong className="text-primary">
              Disable Companion Tooltips:
            </strong>{" "}
            Hide proactive companion suggestions and tooltips that
            appear while you work.
          </div>
          <CompanionTooltipsToggle />
        </AccountItemLine>
      </div>
    </div>
  );
}

function CapitalFormatToggle() {
  const [
    shouldUseCapitalSubtitlesCopy,
    setShouldUseCapitalSubtitlesCopy,
  ] = useLocalStorage("should-use-capital-subtitles-copy");

  const handleToggle = () => {
    const newValue = !shouldUseCapitalSubtitlesCopy;
    setShouldUseCapitalSubtitlesCopy(newValue);
    mixpanel.track("Updated Account Setting", {
      settingName: "Capitalize SOAP titles",
      settingKey: "should-use-capital-subtitles-copy",
      settingValue: newValue,
    });
  };

  return (
    <div className="flex flex-row items-center gap-sm cursor-pointer">
      <p className="text-xs text-secondary text-nowrap">
        Don't Capitalize
      </p>
      <DSToggle
        checked={shouldUseCapitalSubtitlesCopy}
        onChange={handleToggle}
        dataTestId="capitalize-soap-toggle"
        aria-label="Capitalize SOAP Titles"
      />
      <p className="text-xs text-secondary text-nowrap">Capitalize</p>
    </div>
  );
}

function CompanionTooltipsToggle() {
  const disabled = useCompanionTooltipsDisabled();
  const { setDisabled, loading } = useSetCompanionTooltipsDisabled();

  const handleToggle = async () => {
    const newValue = !disabled;
    try {
      const updated = await setDisabled(newValue);
      if (!updated) return;
      mixpanel.track("Updated Account Setting", {
        settingName: "Disable Companion Tooltips",
        settingKey: "disable-companion-tooltips",
        settingValue: newValue,
      });
    } catch {
      alert(
        "error",
        "Failed to save preference. Please try again later.",
      );
    }
  };

  return (
    <DSToggle
      checked={disabled}
      onChange={handleToggle}
      disabled={loading}
      dataTestId="companion-tooltips-toggle"
      aria-label="Disable Companion Tooltips"
    />
  );
}

function CopySubmitToggle() {
  const [shouldCopyOnSubmit, setShouldCopyOnSubmit] = useLocalStorage(
    "should-copy-on-submit",
    true,
  );

  const handleToggle = () => {
    const newValue = !shouldCopyOnSubmit;
    setShouldCopyOnSubmit(newValue);
    mixpanel.track("Updated Account Setting", {
      settingName: "Copy Note On Submit",
      settingKey: "should-copy-on-submit",
      settingValue: newValue,
    });
  };

  return (
    <div className="flex flex-row items-center gap-sm cursor-pointer">
      <p className="text-xs text-secondary text-nowrap">Don't Copy</p>
      <DSToggle
        checked={shouldCopyOnSubmit}
        onChange={handleToggle}
        dataTestId="copy-on-submit-toggle"
        aria-label="Copy Note on Submit"
      />
      <p className="text-xs text-secondary text-nowrap">Copy</p>
    </div>
  );
}
